<script>
  import { format, isValid, parseISO } from "date-fns";
  import { navigate } from "svelte-routing";
  import Button from "../../Common/Button.svelte";
  import getSectorNames from "../../../helpers/getSectorNames";
  import findSummaryById from "../../../helpers/findSummaryById";
  import { rememberSummaryEntry } from "../../../helpers/rememberSummaryEntry";
  import cleanUpApiString from "../../../helpers/cleanUpApiString";
  import DownloadIcon from "../../Common/Icons/DownloadIcon.svelte";
  import PodcastIcon from "../../Common/Icons/PodcastIcon.svelte";
  import CashIcon from "../../Common/Icons/CashIcon.svelte";
  import HatIcon from "../../Common/Icons/HatIcon.svelte";
  import ClipboardIcon from "../../Common/Icons/ClipboardIcon.svelte";
  import DocIcon from "../../Common/Icons/DocIcon.svelte";
  import ChartIcon from "../../Common/Icons/ChartIcon.svelte";
  import PercentIcon from "../../Common/Icons/PercentIcon.svelte";
  import GaIcon from "../../Common/Icons/GaIcon.svelte";

  // Props
  export let result = null;
  if (result.publishDate?.length) {
    result.publishDate = result.publishDate.split(" ")[0];
  }
  const parentReport =
    result.relatedReports.filter(
      (relatedReport) => relatedReport.type === "parent"
    )?.[0] ?? null;
  if (parentReport?.summaryId)
    parentReport.summary = findSummaryById(parentReport.summaryId);

  const childReport =
    result.relatedReports.filter(
      (relatedReport) => relatedReport.type === "child"
    )?.[0] ?? null;
  if (childReport?.summaryId)
    childReport.summary = findSummaryById(childReport.summaryId);

  // Constants
  const foundSummaryReport = findSummaryById(result?.summaryId);
  const imgSrc = result.featuredImage?.length
    ? `data:image/jpeg;base64,${result.featuredImage}`
    : null;
  let Icon = null;

  switch (result.reportType.name) {
    case "Financial Accreditation Reports":
      Icon = HatIcon;
      break;
    case "Financial Agreed Upon Procedures Reports":
      Icon = ClipboardIcon;
      break;
    case "Financial or Single Audit Reports":
      Icon = CashIcon;
      break;
    case "Other Reports":
      Icon = DocIcon;
      break;
    case "Performance Audit Reports":
      Icon = ChartIcon;
      break;
    case "SPLOST Schedules":
      Icon = PercentIcon;
      break;
    case "Statewide Reports":
      Icon = GaIcon;
      break;

    default:
      Icon = null;
      break;
  }

  // Handlers
  const handleSummaryLink = () => {
    rememberSummaryEntry();
    navigate(`/reports/summaries/${foundSummaryReport.slug}`);
  };
</script>

<div class="search-result">
  <div class="search-result-wrapper">
    <div class={`search-result-main ${imgSrc ? "with-img" : ""}`}>
      {#if result.reportType && result.reportType.name}
        <p class="search-result-type">
          {#if Icon}
            <svelte:component this={Icon} />
          {/if}
          {cleanUpApiString(result.reportType.name)}
          {#if result.fiscalYear}
            {@html " &ndash; "}{result.fiscalYear}
          {/if}
        </p>
      {/if}
      {#if result.title}
        {#if foundSummaryReport}
          <a
            class="title-link"
            href={`/reports/summaries/${foundSummaryReport.slug}`}
            on:click|preventDefault={handleSummaryLink}
          >
            <h4>{result.title}</h4>
          </a>
        {:else if result.url?.length}
          <a class="title-link" href={result.url} target="_blank">
            <h4>{result.title}</h4>
          </a>
        {:else}
          <h4>{result.title}</h4>
        {/if}
      {/if}
      {#if result.description}
        <p class="search-result-desc">{result.description}</p>
      {/if}

      {#if parentReport}
        <p class="search-result-desc">
          Follow-up to
          {#if parentReport.summary?.slug}
            {" "}
            <a href={`/reports/summaries/${parentReport.summary.slug}`}>
              {parentReport.title}
            </a>
          {:else}
            <a href={parentReport.url} target="_blank">{parentReport.title}</a>
          {/if}
        </p>
      {/if}
      {#if childReport}
        <p class="search-result-desc">
          Followed up by
          {#if childReport.summary?.slug}
            {" "}
            <a href={`/reports/summaries/${childReport.summary.slug}`}>
              {childReport.title}
            </a>
          {:else}
            <a href={childReport.url} target="_blank">{childReport.title}</a>
          {/if}
        </p>
      {/if}
      <div class="search-result-links">
        {#if foundSummaryReport}
          <Button
            class="blue"
            href={`/reports/summaries/${foundSummaryReport.slug}`}
            onClick={handleSummaryLink}
          >
            View Summary
          </Button>
        {/if}
        {#if result.podcastUrl}
          <Button href={result.podcastUrl} target="_blank">
            <PodcastIcon />
            <span>Video Summary</span>
          </Button>
        {/if}
        {#if result.url?.length}
          <Button
            href={result.url}
            target="_blank"
            class={!foundSummaryReport ? "blue" : ""}
          >
            <DownloadIcon />
            <span>Full Report</span>
          </Button>
        {/if}
      </div>
    </div>
    {#if imgSrc}
      <div class="image-wrap">
        <img src={imgSrc} alt={result.title} />
      </div>
    {/if}
  </div>
  <div class="search-result-sector">
    {#if result.sectorIds?.length}
      <p>
        {getSectorNames(result.sectorIds)}
      </p>
    {/if}
    {#if result.publishDate?.length && isValid(parseISO(result.publishDate))}
      <p>
        Published {format(parseISO(result.publishDate), "M/d/yy")}
      </p>
    {/if}
  </div>
</div>

<style lang="scss">
  .search-result {
    margin: 35px 0 60px;
  }

  .search-result-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
  }

  .search-result-main.with-img {
    width: calc(100% - 210px - 75px);
  }

  .image-wrap {
    width: 210px;
  }

  .image-wrap img {
    object-fit: cover;
    object-position: center;
    height: 100%;
    width: 100%;
    max-height: 350px;
  }

  .search-result-type {
    margin-bottom: 5px;
    padding: 0;
    font-size: 13px;
    line-height: 24px;
    color: #636768;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    display: flex;
    align-items: center;

    :global(svg) {
      display: inline-flex;
      margin-right: 10px;
    }
  }

  .title-link:hover h4 {
    color: #006b97;
    text-decoration: underline;
  }

  .search-result-main h4 {
    font-family: "Segoe UI", sans-serif;
    font-weight: 500; // Design calls for 600 but 500 looks better because of Segoe UI vs Avenir
    font-size: 24px;
    line-height: 30px;
    color: #4a4a4a;
  }

  .search-result-desc {
    color: #4a4a4a;
  }

  .search-result-links {
    margin-top: 10px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
  }

  .search-result-links :global(a) {
    margin-right: 14px;
    margin-top: 8px;
  }

  .search-result-sector {
    margin-top: 30px;
    padding: 15px;
    background-color: #fafafa;
    border-bottom: 1px solid #e7e5e2;

    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 30px;
  }

  .search-result-sector p {
    padding: 0;
    font-size: 14px;
    line-height: 16px;
    color: #9b9b9b;
  }

  @media screen and (max-width: 1210px) {
    .search-result-main.with-img {
      width: 66%;
      align-items: flex-start;
    }
    .image-wrap {
      width: calc(33% - 20px);
    }
  }
  @media (max-width: 1080px) {
    .search-result-links {
      flex-direction: column;
      align-items: flex-start;

      & > :global(.report-button) {
        justify-content: center;
        width: 240px;
        margin-right: 0;
      }
    }
  }
  @media screen and (max-width: 980px) {
    .search-result {
      margin: 0 0 60px;
    }
    .search-result-sector {
      width: 100vw;
      position: relative;
      left: -30px;
      padding: 15px 30px;
    }
    .search-result-main.with-img {
      width: 60%;
    }
    .image-wrap {
      width: calc(40% - 30px);
    }
  }
  @media screen and (max-width: 640px) {
    .search-result-wrapper {
      flex-direction: column-reverse;
    }
    .search-result-main.with-img,
    .image-wrap {
      width: 100%;
    }
    .image-wrap {
      max-width: 420px;
      margin-bottom: 12px;
    }
  }
</style>
